import React, { useEffect, useState } from 'react';
import { Box, TablePagination, Typography, useTheme } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import GenericTable from '../common/GenericTable';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWishlist, removeFromWishlist } from '../../redux/wishlistSlice';
import { addToCart, fetchCart } from '../../redux/cartSlice';
import { useNotification } from '../../context/NotificationContext';
import { useNavigate } from 'react-router-dom';

const WishList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [wishListProducts, setWishListProducts] = useState([]);
  const [loadingProductId, setLoadingProductId] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const dispatch = useDispatch();
  const { wishItems, loading, error } = useSelector((state) => state.wishlist);
  const { user } = useSelector((state) => state.auth);
  const theme = useTheme();
  const addNotification = useNotification();
const navigate=useNavigate();
  // Fixed columns
  const columns = [
    {
      label: 'Image',
      field: 'image',
      render: (image, row) => <img src={
        row?.product?.images?.find((img) => img.image_type)?.url
      } alt="" width={50} height={50}
      style={{
        objectFit: 'contain',
        borderRadius:"50%"
      }}
      />
    },
    { label: 'Product Name', field: 'product_name',render: (product_name, row) => {
        return (
          <Typography variant="body2" sx={{ fontWeight: 600,
            textDecoration: 'underline',
           }}
           onClick={() => navigate(`/product/detail/${row.product_id}`)}
           >
            {row?.product?.product_name}
          </Typography>
        );
     }
    },
    { label: 'Stock', field: 'current_stocks',
      render: (current_stocks,row) => (
        <Typography>
          {row?.product?.current_stocks || 'N/A'}
        </Typography>
      )
     },
    {
      label: 'Price',
      field: 'latest_price',
      render: (latest_price) => latest_price ? `${latest_price}` : 'N/A'
    }
  ];
  
  const actions = [
    {
      icon: <ShoppingCartIcon />,
      onClick: (row) => handleAddToCart(row),
      style: { backgroundColor: theme.palette.primary.gray, minWidth: '40px', padding: '6px' }
    },
    {
      icon: <CloseIcon />,
      onClick: (row) => handleRemoveFromWishlist(row),
      style: { backgroundColor: theme.palette.primary.red, minWidth: '40px', padding: '6px' }
    }
  ];

  const handleAddToCart = async (product) => {
    try {
      setLoadingProductId(product.id);
      const response = await dispatch(addToCart({ 
        userId: user?.id, 
        product_id: product.product_id ,
        quantity:product?.quantity || 1
      }));
      
      if (addToCart.fulfilled.match(response)) {
        addNotification('success', 'Product added to cart');
        await dispatch(fetchCart())
      }
    } catch (error) {
      addNotification('error', 'Failed to add product to cart');
    } finally {
      setLoadingProductId(null);
    }
  };

  const handleRemoveFromWishlist = (product) => {
    setLoadingProductId(product.id);

    dispatch(removeFromWishlist(product.id))
      .then(() => {
        const currentPageProducts = wishListProducts.length;
        let newPage = page;
        
        if (currentPageProducts <= 1 && page > 0) {
          newPage = page - 1;
          setPage(newPage);
        }

        dispatch(fetchWishlist({ 
          userId: user?.id, 
          rowsPerPage, 
          page: newPage + 1 
        }));

        addNotification('success', 'Product removed from wishlist');
      })
      .catch((error) => {
        addNotification('error', 'Failed to remove product from wishlist');
      })
      .finally(() => {
        setLoadingProductId(null);
      });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    setIsInitialLoad(true);
    dispatch(fetchWishlist({ 
      userId: user?.id, 
      rowsPerPage,
      page: newPage + 1 
    }));
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setIsInitialLoad(true);
    dispatch(fetchWishlist({ 
      userId: user?.id, 
      rowsPerPage: newRowsPerPage, 
      page: 1 
    }));
  };

  useEffect(() => {
    if (user?.id) {
      setIsInitialLoad(true);
      dispatch(fetchWishlist({ 
        userId: user.id, 
        rowsPerPage, 
        page: page + 1 
      }));
    }
  }, [user, rowsPerPage]);
  
  useEffect(() => {
    if (wishItems) {
      const productsData = wishItems?.data || [];
      
      if (productsData.length === 0 && page > 0) {
        const newPage = page - 1;
        setPage(newPage);
        dispatch(fetchWishlist({ 
          userId: user?.id, 
          rowsPerPage, 
          page: newPage + 1 
        }));
        return;
      }

      setWishListProducts(productsData);
      setTotalRecords(wishItems?.meta?.total || 0);
      setPage(wishItems?.meta?.current_page - 1 || 0);
      
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    }
  }, [dispatch,wishItems, isInitialLoad]);

  const isRowLoading = (row) => loadingProductId === row.id;

  return (
    <Box
      sx={{
        mt: 2,
        display: 'flex',
        flexDirection: 'column',
        height: 'auto',
        flexGrow: 1,
        gap: 1,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          color: theme.palette.heading_color.main,
          fontWeight: 600,
          fontSize: { xs: 20, sm: 24 },
        }}
      >
        My Wish List
      </Typography>

      {/* Reusable Table */}
      <GenericTable
        columns={columns}
        data={wishListProducts}
        actions={actions}
        renderActions={(row) => !row.isSummary}
        loading={isInitialLoad}
        rowLoading={isRowLoading}
      />

      {/* Pagination */}
      <TablePagination
        component="div"
        count={totalRecords}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Box>
  );
};

export default WishList;